/**
 * Whether the Electron main process still holds the desktop session.
 *
 * On desktop, `ElectronAPI.getAccessToken()` resolves `null` both when the
 * user is signed out and when an expired access token cannot be refreshed
 * because Auth0 is unreachable (WAN down in a local mode session). Only the
 * first one is a sign-out. Main keeps the stored session through offline
 * refresh failures, so its `get-user-info` status tells the two apart without
 * touching the network: a stored session answers from the userInfo cache with
 * `authenticated` / `pending`, or `NETWORK_ERROR` when there is no cache yet.
 *
 * Anything else (`unauthenticated`, a legacy response without a status, no
 * IPC) reads as "not held", which keeps the old null -> logout behavior.
 *
 * Calls the IPC channel directly instead of `ElectronAPI.getUserInfo` so the
 * synchronizer does not pull the whole Electron API module graph into its
 * import set (and every synchronizer test's mock surface).
 */
export const isDesktopSessionHeld = async (): Promise<boolean> => {
  try {
    const result: unknown = await window.electron?.ipcRenderer?.invoke('get-user-info');
    if (!result || typeof result !== 'object') return false;
    const { status, error } = result as { status?: unknown; error?: unknown };
    return status === 'authenticated' || status === 'pending' || error === 'NETWORK_ERROR';
  } catch {
    return false;
  }
};
